import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, Loader2, PenLine, Route as RouteIcon, Sparkles, Target } from "lucide-react";
import { Confetti } from "@/components/confetti";
import { buildPlan, GOALS_BY_STATUS, WORK_GOALS } from "@/lib/personal-plan";
import {
  updateCoachProfile,
  generateCoachPlan,
  startPracticeSession,
  getScenarios,
} from "@/data/requests/practice-requests";
import { useQuery } from "@tanstack/react-query";

const PLUM = "#1E293B";
const TOTAL_STEPS = 5;
const STATUS_KEYS = ["single", "dating", "relationship", "married", "breakup"];
const CONTEXT_KEYS = ["dating-apps", "define", "home-conflict", "work", "family", "friendship"];
const SKILL_KEYS = ["boundaries", "expressing-feelings", "conflict", "texting"];

// A guided "intake session" with the coach: 5 quick steps -> a personal plan.
export function PersonalSetupScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Prefill from the saved profile so a check-in re-run is a few quick clicks.
  const existing = queryClient.getQueryData(["coach-profile"])?.data?.personal || {};
  const [step, setStep] = useState(0);
  const [stage, setStage] = useState("steps"); // steps | plan
  const [status, setStatus] = useState(existing.status || null);
  const [contexts, setContexts] = useState(Array.isArray(existing.contexts) ? existing.contexts : []);
  const [goal, setGoal] = useState(existing.goal || null);
  const [skill, setSkill] = useState(null);
  const [freeText, setFreeText] = useState(existing.freeText || "");

  const goalOptions = useMemo(() => {
    const base = GOALS_BY_STATUS[status] || [];
    return contexts.includes("work") ? [...base, ...WORK_GOALS] : base;
  }, [status, contexts]);

  const personal = { status, contexts, goal, freeText: freeText.trim() || undefined };

  // AI plan with instant rule-based fallback.
  const [aiPlan, setAiPlan] = useState(null);
  const [isBuildingPlan, setIsBuildingPlan] = useState(false);
  const fallbackPlan = useMemo(() => buildPlan(personal), [status, contexts, goal, freeText]);
  const plan = aiPlan?.steps?.length ? aiPlan.steps : fallbackPlan;

  const { data: scenariosData } = useQuery({ queryKey: ["scenarios"], queryFn: getScenarios });
  const scenarios = scenariosData?.data || [];

  const saveMutation = useMutation({
    mutationFn: () =>
      updateCoachProfile({
        personal: { ...personal, savedAt: new Date().toISOString() },
        focusAreas: skill ? [skill] : undefined,
      }),
    onSuccess: async () => {
      setStage("plan");
      setIsBuildingPlan(true);
      try {
        const res = await generateCoachPlan(i18n.language);
        if (res.data?.steps?.length) setAiPlan(res.data);
      } catch {
        // fallback plan already covers the screen
      } finally {
        setIsBuildingPlan(false);
        queryClient.invalidateQueries({ queryKey: ["coach-profile"] });
      }
    },
  });

  const advance = () => {
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
    else saveMutation.mutate();
  };
  const pickAndAdvance = (setter, value) => {
    setter(value);
    setTimeout(advance, 150);
  };
  const toggleContext = (key) =>
    setContexts((cur) => (cur.includes(key) ? cur.filter((c) => c !== key) : [...cur, key]));

  const startMutation = useMutation({
    mutationFn: (payload) => startPracticeSession(payload, i18n.language),
    onSuccess: (res) => navigate(`/practice/${res.data.id}`),
  });

  // ---- PLAN (the payoff: coach responds + shows the path) ----
  if (stage === "plan") {
    const first = plan[0];
    const startFirst = () => {
      if (!first) return navigate("/practice");
      if (first.type === "journey") return navigate("/journeys");
      if (first.type === "own")
        return startMutation.mutate({ situation: t(`personalSetup.prefills.${first.prefillKey}`) });
      const sc = scenarios.find((s) => s.slug === first.slug);
      if (sc) return startMutation.mutate({ scenarioId: sc.id, personalize: true });
      navigate("/practice");
    };
    return (
      <AppLayout title={t("personalSetup.title")}>
        <div className="max-w-2xl">
          <Confetti />
          <div className="rounded-2xl p-6 text-white mb-5" style={{ backgroundColor: PLUM }}>
            <Sparkles className="h-8 w-8 mb-2" />
            <h2 className="text-2xl font-semibold mb-1">{t("personalSetup.planTitle")}</h2>
            <p className="opacity-90 text-sm font-semibold">
              {t("personalSetup.planRecap", {
                status: t(`personalSetup.status.${status}`),
                goal: t(`personalSetup.goals.${goal}`),
              })}
            </p>
          </div>

          <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: PLUM + "14" }}>
            {isBuildingPlan ? (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: PLUM }} />
                {t("personalSetup.planBuilding")}
              </div>
            ) : (
              <p className="text-sm leading-relaxed font-medium">
                {aiPlan?.coachMessage
                  ? aiPlan.coachMessage
                  : personal.freeText
                    ? t("personalSetup.planCoachEcho", {
                        quote: personal.freeText.length > 90 ? personal.freeText.slice(0, 90) + "…" : personal.freeText,
                      })
                    : t("personalSetup.planCoach")}
              </p>
            )}
          </div>

          <div className="space-y-3 mb-5">
            {plan.map((rec, i) => (
              <div
                key={rec.key}
                className="flex items-center gap-3 rounded-xl border bg-card p-4"
                style={{ borderColor: i === 0 ? PLUM : undefined, borderWidth: i === 0 ? 1.5 : 1 }}
              >
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold shrink-0 text-white"
                  style={{ backgroundColor: i === 0 ? PLUM : "hsl(var(--muted-foreground))" }}
                >
                  {i + 1}
                </span>
                {rec.type === "journey" ? (
                  <RouteIcon className="h-4 w-4 shrink-0" style={{ color: PLUM }} />
                ) : rec.type === "own" ? (
                  <PenLine className="h-4 w-4 shrink-0" style={{ color: PLUM }} />
                ) : (
                  <Target className="h-4 w-4 shrink-0" style={{ color: PLUM }} />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-sm">
                    {rec.type === "journey"
                      ? `${t("practice.forYou.journeyTag")}: ${t(`journeys.list.${rec.journeyId}.name`)}`
                      : rec.type === "own"
                        ? `${t("practice.forYou.ownTag")}: ${t(`personalSetup.goals.${goal}`)}`
                        : t(`practice.scenarios.${rec.slug}.title`, { defaultValue: rec.slug })}
                  </span>
                  {rec.why ? (
                    <span className="block text-xs text-muted-foreground mt-0.5">{rec.why}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>

          <Button
            className="w-full text-white"
            style={{ backgroundColor: PLUM }}
            disabled={startMutation.isPending}
            onClick={startFirst}
          >
            {startMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronRight className="mr-2 h-4 w-4" />
            )}
            {t("personalSetup.planStart")}
          </Button>
          <button
            className="block w-full text-center text-sm text-muted-foreground mt-3 py-2"
            onClick={() => navigate("/dashboard")}
          >
            {t("personalSetup.planLater")}
          </button>
        </div>
      </AppLayout>
    );
  }

  // ---- STEPS ----
  const qKey = ["q1", "q2", "q3", "q4", "q5"][step];
  const Option = ({ label, selected, onClick }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between rounded-xl border bg-card px-4 py-3.5 text-left font-medium transition hover:shadow-sm"
      style={{ borderColor: selected ? PLUM : undefined, color: selected ? PLUM : undefined, borderWidth: selected ? 1.5 : 1 }}
    >
      {label}
      {selected ? <Check className="h-4 w-4" style={{ color: PLUM }} /> : null}
    </button>
  );

  return (
    <AppLayout title={t("personalSetup.title")}>
      <div className="max-w-xl">
        <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-2">
          <div
            className="h-1.5 rounded-full transition-all"
            style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%`, backgroundColor: PLUM }}
          />
        </div>
        <p className="text-xs font-semibold text-muted-foreground mb-6">
          {t("personalSetup.stepOf", { n: step + 1, total: TOTAL_STEPS })}
        </p>

        <h2 className="text-xl font-semibold mb-1">{t(`personalSetup.${qKey}`)}</h2>
        <p className="text-sm text-muted-foreground mb-5">{t(`personalSetup.${qKey}Hint`)}</p>

        <div className="space-y-2.5">
          {step === 0 &&
            STATUS_KEYS.map((k) => (
              <Option key={k} label={t(`personalSetup.status.${k}`)} selected={status === k} onClick={() => pickAndAdvance(setStatus, k)} />
            ))}
          {step === 1 &&
            CONTEXT_KEYS.map((k) => (
              <Option key={k} label={t(`personalSetup.contexts.${k}`)} selected={contexts.includes(k)} onClick={() => toggleContext(k)} />
            ))}
          {step === 2 &&
            goalOptions.map((k) => (
              <Option key={k} label={t(`personalSetup.goals.${k}`)} selected={goal === k} onClick={() => pickAndAdvance(setGoal, k)} />
            ))}
          {step === 3 &&
            SKILL_KEYS.map((k) => (
              <Option key={k} label={t(`coachAreas.${k}`, { defaultValue: k })} selected={skill === k} onClick={() => pickAndAdvance(setSkill, k)} />
            ))}
          {step === 4 && (
            <textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value.slice(0, 400))}
              placeholder={t("personalSetup.q5Ph")}
              rows={4}
              className="w-full rounded-xl border bg-card p-3.5 text-sm outline-none focus:ring-2"
              style={{ "--tw-ring-color": PLUM }}
            />
          )}
        </div>

        {(step === 1 || step === 4) && (
          <Button
            className="w-full mt-5 text-white"
            style={{ backgroundColor: PLUM }}
            disabled={saveMutation.isPending}
            onClick={advance}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronRight className="mr-2 h-4 w-4" />
            )}
            {step === 4 ? t("personalSetup.finish") : t("personalSetup.next")}
          </Button>
        )}
        {step === 4 && !freeText.trim() && (
          <button className="block w-full text-center text-sm text-muted-foreground mt-3 py-2" onClick={advance}>
            {t("personalSetup.skip")}
          </button>
        )}
      </div>
    </AppLayout>
  );
}
